import React from 'react';
import * as Yup from 'yup';
import { Button, Dialog, DialogProps } from '@economic/taco';

import { Trans } from '@lingui/macro';
import { Column, Row } from '../../shared/components/Grid/Grid';
import { Form, Field } from '../../shared/components/Form';
import { FormProps } from '../types';
import internalApiClient from '../../shared/util/api/internalApiClient';
import { t, useLocalization } from '../../shared/components/l10n/l10n';
import Loader from '../../shared/components/Loader';
import { Location } from '../../../types/internal-api';

type LocationFormProps = FormProps<Location>;
type LocationModalProps = Omit<DialogProps, 'children'> & LocationFormProps;

const initialValues: Partial<Location> = {
    number: undefined,
    name: undefined,
    costPriceMarkupPct: undefined,
};

const validationSchema = Yup.object({
    number: Yup.string()
        .required()
        .test({
            name: 'number',
            async test(value) {
                if (!/^\d+$/.test(value)) {
                    return this.createError({
                        message: t`SETTINGS_LEDGER_NUMERIC_KEYS_ONLY`,
                    });
                }
                return true;
            },
        }),

    name: Yup.string().required(),
    costPriceMarkupPct: Yup.number().nullable(),
});

// Todos: add proper types
export default function CreateLocationForm({
    initialData = initialValues,
    notifications,
    onSubmitted,
    onSubmitError,
    onCancel,
}: LocationFormProps) {
    const loading = useLocalization(textIds);

    const handleSubmit = async values => {
        try {
            const response = await internalApiClient.post(`/inventory/locations`, values);
            return Promise.resolve(response);
        } catch (error) {
            error.validation = error.response.data;
            return Promise.reject(error);
        }
    };

    return loading ? (
        <div className="h-40">
            <Loader />
        </div>
    ) : (
        <Form
            initialData={initialData}
            messages={notifications}
            onSubmit={handleSubmit}
            onSubmitted={onSubmitted}
            onSubmitError={onSubmitError}
            validationSchema={validationSchema}>
            <h1>{t`FORM_LOCATION_HEADLINE`}</h1>

            <Row>
                <Column>
                    <Field.Text label={t`FORM_FIELD_LABEL_NUMBER`} name="number" required />
                </Column>
                <Column>
                    <Field.Text label={t`FORM_FIELD_LABEL_NAME`} name="name" required />
                </Column>
                <Column>
                    <Field.Number decimal label={t`FORM_ACTIVITIES_EDITFIELD_COSTPRICEMARKUPPCT`} name="costPriceMarkupPct" />
                </Column>
            </Row>

            <div className="form__buttons">
                <Button type="submit" appearance="primary">
                    <Trans>Save</Trans>
                </Button>
                {onCancel && (
                    <Button onClick={onCancel} appearance="default">
                        <Trans>Cancel</Trans>
                    </Button>
                )}
            </div>
        </Form>
    );
}

export const CreateLocationModal = ({ onSubmitted: handleSubmitted, ...modalProps }: LocationModalProps) => {
    return (
        <Dialog {...modalProps} size="sm">
            <Dialog.Content aria-label={t`FORM_LOCATION_CREATETEXT`}>
                <CreateLocationForm
                    onSubmitted={Location => {
                        if (handleSubmitted) {
                            handleSubmitted(Location);
                        }
                    }}
                />
            </Dialog.Content>
        </Dialog>
    );
};

const textIds = ['FORM_ACTIVITIES_EDITFIELD_COSTPRICEMARKUPPCT', 'SETTINGS_LEDGER_NUMERIC_KEYS_ONLY'];
